import { LitElement, html, css, unsafeCSS } from 'lit';
import logoFullUrl from '../assets/logo-full.png';
import { CONFIG } from '../config.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import {
  Briefcase,
  FlaskConical,
  UserCheck,
  Settings2,
  Settings,
  Folder,
  Users,
  X,
  Plus,
  Share,
  Download,
  AlertTriangle,
  FileJson,
  ArrowLeft,
  LogOut,
  Pencil,
  Trash2,
  Search,
  Building2,
  Smartphone,
  Sun,
  Moon,
  ChevronsUpDown,
  ChevronRight,
  ChevronLeft,
  Check,
  UsersRound,
  Activity,
  ChevronDown,
} from 'lucide';
import { EXTRACTION_PROFILES } from '@mv/models';
import { getCodelistDomains } from '../data/codelist-registry.js';
import { tipMove, tipHide } from '../utils/hover-tip.js';
import { api } from '../api.js';
import { auth, COMPANIES_EVENT } from '../auth.js';
import { confirmDialog, alertDialog } from '../utils/confirm-dialog.js';
import { identifyUser, capture, resetAnalytics } from '../analytics.js';
import { setSentryUser, clearSentryUser } from '../sentry.js';
import './project-dashboard.js';
import './project-detail.js';
import './lab-koppeling-page.js';
import './lab-order-page.js';
import './templates-page.js';
import { TEMPLATE_NAME_EVENT } from './template-editor.js';
import './login-form.js';

import { normalizeExtractionProfile } from '@mv/models';

import './admin-users.js';
import './company-dashboard.js';
import './report-settings-page.js';
import { betaFeatureAllowed } from '@mv/models';
import { profielUitOpslag, profielOnthouden } from '../utils/bedrijfsprofiel-cache.js';
import './analysis-page.js';
import './validation-page.js';
import './recorder-app.js';
import './recorder-settings.js';
import './settings-nav.js';
import './company-settings.js';
import './texture-settings.js';
import './admin-global-settings.js';
import './validation-model-editor.js';
import './team-page.js';
import './accept-invite.js';
import './reset-password.js';
import './personal-settings.js';
// Named import én registratie in één: het component definieert zichzelf bij het laden, en de
// sleutel komt hiervandaan zodat pagina en schil hem niet los van elkaar kunnen spellen.
import { BREADCRUMBS_PREF, FANCY_BG_PREF } from './personal-appearance.js';
import styles from '../styles/app-main.css?inline';

import { lucideIcon } from '../icon.js';
import { initials, displayName, avatarColor } from '../utils/avatar.js';
import { setCompanyTextures } from '../utils/company-textures.js';


/** De instellingenpagina's onder 'Bedrijf': alleen voor een bedrijfsbeheerder. */
const COMPANY_SETTINGS_VIEWS = ['recorder-settings', 'company-settings', 'templates', 'template-editor', 'report-settings', 'texture-settings'];

/** Rollabel in de bedrijvenkiezer, in kapitalen zoals het LEZEN-label bij meekijken. */
const ROLE_TAG = { owner: 'EIGENAAR', manager: 'BEHEERDER', member: 'LID', guest: 'GAST' };

export class AppMain extends LitElement {
  static styles = css`${unsafeCSS(styles)}`;

  static properties = {
    currentView: { type: String },
    selectedProjectId: { type: String },
    // Het extractiemodel uit /admin/validation/:model. Apart reactief, want bij een tabwissel
    // blijft currentView 'validation' — zonder dit rendert Lit niet opnieuw.
    validationProfile: { type: String },
    // Gekozen codelijst-domein uit /admin/settings/codelists/:id — om dezelfde reden apart reactief.
    codelistDomain: { type: String },
    projectTab: { type: String },     // subpagina van een project (/projects/:id/:tab)
    projectFocus: { type: Object },   // ?boring=<id>&tab=<subtab> — diepe link naar één boring
    labOrderId: { type: String },     // /labopdrachten/:id
    labOrderReturn: { type: String }, // ?terug=alle — waar de pijl terug heen gaat
    // Licentie van het actieve bedrijf: 'geovoice' | 'geodesk'. Bepaalt of Labopdrachten in
    // het hoofdmenu staat. Begint bij wat we van dit bedrijf onthouden hebben en wordt daarna
    // opgehaald; weten we niets, dan blijft het item weg tot het antwoord er is -- een
    // menu-item dat verschijnt en weer verdwijnt leest als een storing.
    _companyLicense: { state: true },
    _templateName: { state: true },     // naam van het sjabloon in de editor, voor het kruimelpad
    _betaFeatures: { state: true },  // aangezette bètafuncties van het bedrijf (voor menu-items)
    _searchQ: { state: true },
    _searchGroups: { state: true },
    _searchOpen: { state: true },
    _searchBusy: { state: true },
    _crumbMenuOpen: { state: true },
    // De bedrijven waar je in mag. Reactief, want de lijst kan tijdens je sessie groeien:
    // een beheerder voegt je toe, of je voegt jezelf toe vanuit /admin/companies.
    _companies: { state: true },
    _recentSearches: { state: true },
    projects: { type: Array },
    loading: { type: Boolean },
    error: { type: String },
    isAuthenticated: { type: Boolean },
    needsCompanySelection: { type: Boolean },
    companySwitchError: { type: String },
    companyMenuOpen: { type: Boolean },
    profileMenuOpen: { type: Boolean },
    _profile: { type: Object },
    sessionExpiredMessage: { type: String },
    searchQuery: { type: String },
    topbarActions: { type: Array },
    breadcrumbs: { type: Array },
    _showCrumbs: { state: true },   // persoonlijke voorkeur; standaard aan
    _fancyBg: { state: true },      // persoonlijke voorkeur; standaard UIT
    childBreadcrumbs: { type: Array },
    isDarkMode: { type: Boolean },
    _navOpen: { type: Boolean, state: true },
    initialParentId: { type: String },
    projectToEdit: { type: Object },
  };

  constructor() {
    super();
    this.currentView = this.getRouteView();
    this.projects = [];
    this.selectedProjectId = this.getRouteProjectId();
    this.validationProfile = this.getRouteValidationProfile();
    this.codelistDomain = this.getRouteCodelistDomain();
    this.projectTab = this.getRouteProjectTab();
    this.projectFocus = this.getRouteProjectFocus();
    this.labOrderId = this.getRouteLabOrderId();
    this.labOrderReturn = this.getRouteLabOrderReturn();
    this._companyLicense = this._licentieUitOpslag();
    this._betaFeatures = profielUitOpslag()?.betaFeatures ?? undefined;
    this._searchQ = '';
    this._searchGroups = [];
    this._searchOpen = false;
    this._searchBusy = false;
    this._crumbMenuOpen = null;
    this._recentSearches = [];
    this.isAuthenticated = auth.isAuthenticated();
    this.sessionExpiredMessage = null;
    this.searchQuery = '';
    this.isDarkMode = localStorage.getItem('mv_theme') === 'dark';
    // Uitklappen is een KEUZE, geen muisbeweging — dus onthouden we hem.
    this._navOpen = localStorage.getItem('mv_nav_open') === '1';
    this.applyTheme();
    this.initialParentId = null;
    this.projectToEdit = null;
    this.profileMenuOpen = false;
    // Seed the profile from the login response; refreshed from the backend on mount.
    const u = auth.getUser();
    this._profile = u ? { firstName: u.first_name || '', lastName: u.last_name || '', email: u.email || '', avatarUpdatedAt: u.avatar_updated_at || null } : null;
  }

  /** Authoritative name + avatar for the sidebar/menu (reflects edits immediately). */
  async _loadProfile() {
    if (!auth.isAuthenticated()) return;
    try {
      const p = await api.getPersonalProfile();
      this._profile = { firstName: p.firstName || '', lastName: p.lastName || '', email: p.email || '', avatarUpdatedAt: p.avatarUpdatedAt || null };
    } catch (_) { /* keep the login-seeded profile on failure */ }
  }

  /**
   * Licentie van het actieve bedrijf, voor het menu.
   *
   * Labopdrachten is een GeoDesk-onderdeel. Binnen een project blijft het tabblad wél staan --
   * daar zie je de upgrademelding in de context van je eigen werk -- maar in het hoofdmenu
   * hoort geen ingang naar iets wat je niet hebt. Mislukt het ophalen, dan geldt de
   * basislicentie en blijft het item dus weg.
   */
  async _loadLicense() {
    if (!auth.isAuthenticated()) return;
    try {
      const p = await api.getCompanyProfile();
      this._companyLicense = p?.license || 'geovoice';
      this._betaFeatures = p?.betaFeatures || [];
      profielOnthouden({ license: this._companyLicense, betaFeatures: this._betaFeatures });
      this._onthoudLicentie(this._companyLicense);
    } catch (_) { this._companyLicense = 'geovoice'; }
  }

  /**
   * De laatst bekende licentie, per bedrijf, in localStorage.
   *
   * Puur om te voorkomen dat het menu-item bij elke paginalading een tel later in beeld springt
   * -- het ophalen kost een verzoek en het menu staat er meteen. Dit is géén grens: wie deze
   * waarde in zijn browser omzet krijgt hooguit een menu-item dat naar de upgrademelding leidt,
   * want de backend weigert elke labroute zonder GeoDesk (zie requireGeoDesk).
   */
  get _licentieSleutel() {
    const id = auth.getActiveCompany()?.id;
    return id ? `mv_license_${id}` : null;
  }

  _onthoudLicentie(waarde) {
    const sleutel = this._licentieSleutel;
    try { if (sleutel) localStorage.setItem(sleutel, waarde); } catch (_) { /* privémodus */ }
  }

  _licentieUitOpslag() {
    const sleutel = this._licentieSleutel;
    try { return sleutel ? localStorage.getItem(sleutel) : null; } catch (_) { return null; }
  }

  /** Bètafunctie Rapportage: GeoDesk én aangezet (zie @mv/models betaFeatures). */
  get _hasReportBeta() {
    return betaFeatureAllowed({ license: this._companyLicense, betaFeatures: this._betaFeatures }, 'rapportage');
  }

  /** Mag dit bedrijf bij de labkoppeling? Stuurt alleen wat de schil toont. */
  get _hasGeoDesk() {
    return this._companyLicense === 'geodesk';
  }

  getRouteView() {
    const path = window.location.pathname.replace(/^\//, '');
    if (!path || path === '' || path === 'projects') return 'projects';
    if (path.startsWith('projects/')) return 'project-detail';
    // Labopdrachten staan los van de projecten: één lijst over alle projecten heen, en een
    // opdracht met een eigen adres dat je kunt delen.
    if (path === 'labopdrachten') return 'lab-orders';
    if (path.startsWith('labopdrachten/')) return 'lab-order';
    if (path === 'settings' || path === 'settings/recorder') return 'recorder-settings';
    if (path === 'settings/company') return 'company-settings';
    if (path === 'settings/textures') return 'texture-settings';
    if (path === 'personal/settings') return 'personal-settings';
    if (path === 'personal/appearance') return 'personal-appearance';
    if (path === 'templates') return 'templates';
    if (path === 'settings/report') return 'report-settings';
    if (path === 'templates/new' || path.startsWith('templates/')) return 'template-editor';
    // Admin area lives under /admin/* so the whole namespace can be guarded as one.
    if (path.startsWith('admin/')) {
      if (path === 'admin/users') return 'users';
      if (path === 'admin/companies') return 'bedrijven';
      if (path === 'admin/analysis') return 'analysis';
      // /admin/validation/<model> = de subpagina van één extractiemodel; zonder model
      // valt 'ie terug op het default-model (zie getRouteValidationProfile).
      if (path.startsWith('admin/validation/model/')) return 'validation-model-editor';
      if (path === 'admin/validation' || path.startsWith('admin/validation/')) return 'validation';
      // Settings hub (recorder text + model/prompt config). Old /admin/recorder → recorder tab.
      if (path === 'admin/settings' || path === 'admin/settings/recorder' || path === 'admin/recorder') return 'admin-settings-recorder';
      if (path === 'admin/settings/model') return 'admin-settings-model';
      if (path === 'admin/settings/required-fields') return 'admin-settings-required-fields';
      // Codelijsten is een tab van de settings hub; oude links naar /admin/codelists blijven werken.
      if (/^admin\/(settings\/)?codelists(\/|$)/.test(path)) return 'admin-settings-codelists';
      return 'admin-unknown';
    }
    return path;
  }

  /** Het :id uit /labopdrachten/:id, of null op de lijst. */
  getRouteLabOrderId() {
    const path = window.location.pathname.replace(/^\//, '');
    return path.startsWith('labopdrachten/') ? (path.split('/')[1] || null) : null;
  }

  /** Waar de pijl terug op de opdrachtpagina heen gaat: 'alle' of (standaard) het project. */
  getRouteLabOrderReturn() {
    return new URLSearchParams(window.location.search).get('terug') || '';
  }

  getRouteProjectId() {
    const path = window.location.pathname.replace(/^\//, '');
    if (path.startsWith('projects/')) {
      return path.split('/')[1];
    }
    return null;
  }

  /**
   * De subpagina uit /projects/:id/:tab. Geen tab in de URL betekent het overzicht -- dat is
   * het startpunt van een project, en zo blijft een oude link naar /projects/:id werken.
   */
  getRouteProjectTab() {
    const parts = window.location.pathname.replace(/^\//, '').split('/');
    if (parts[0] !== 'projects' || !parts[1]) return null;
    const tab = parts[2] || 'overzicht';
    return ['overzicht', 'vooronderzoek', 'opdracht', 'borings', 'map', 'labopdrachten', 'rapportage', 'export'].includes(tab) ? tab : 'overzicht';
  }

  /**
   * Diepe link naar één boring: ?boring=<id>&tab=<subtab>.
   *
   * In de query en niet in het pad, omdat het geen andere PAGINA is maar een andere positie
   * op dezelfde pagina. Zo blijft /projects/:id/borings het adres van de lijst, en kan een
   * zoekresultaat er een boring en subtab aan toevoegen zonder een tweede route te vragen.
   */
  getRouteProjectFocus() {
    const q = new URLSearchParams(window.location.search);
    const boringId = q.get('boring');
    if (!boringId) return null;
    return { boringId, tab: q.get('tab') || null };
  }

  /** The :id from /admin/validation/model/:id (prompt-version editor), else null. */
  getRouteModelId() {
    const path = window.location.pathname.replace(/^\//, '');
    if (path.startsWith('admin/validation/model/')) return path.split('/')[3] || null;
    return null;
  }

  /** Het domein-id uit /admin/settings/codelists/:id (of het oude /admin/codelists/:id), of null op het overzicht. */
  getRouteCodelistDomain() {
    const path = window.location.pathname.replace(/^\//, '');
    return path.match(/^admin\/(?:settings\/)?codelists\/([^/]+)/)?.[1] || null;
  }

  /** Het extractiemodel uit /admin/validation/:model (default = NEN 5104). */
  getRouteValidationProfile() {
    const path = window.location.pathname.replace(/^\//, '');
    return normalizeExtractionProfile(path.startsWith('admin/validation/') ? path.split('/')[2] : null);
  }

  /** The sjabloon id (or 'new') from /templates/:id — else null. */
  getRouteTemplateId() {
    const path = window.location.pathname.replace(/^\//, '');
    if (path.startsWith('templates/')) return path.split('/')[1] || null;
    return null;
  }

  connectedCallback() {
    super.connectedCallback();

    this.routeChangeListener = () => this.handleRouteChange();
    window.addEventListener('popstate', this.routeChangeListener);
    this._onTemplateName = (e) => { this._templateName = e.detail?.name || ''; };
    window.addEventListener(TEMPLATE_NAME_EVENT, this._onTemplateName);

    setTimeout(() => this.handleRouteChange(), 0);

    this._authExpiredHandler = (e) => {
      this.handleAuthExpired(e.detail.message);
    };
    window.addEventListener('auth-expired', this._authExpiredHandler);

    // Close the company switcher + profile menu when clicking anywhere outside them.
    this._closeMenus = () => {
      if (this.companyMenuOpen) this.companyMenuOpen = false;
      if (this.profileMenuOpen) this.profileMenuOpen = false;
      // De zoeksuggesties horen bij dezelfde afspraak: klikken buiten het venster sluit het.
      if (this._searchOpen) this._searchOpen = false;
      if (this._crumbMenuOpen !== null) this._crumbMenuOpen = null;
    };
    document.addEventListener('click', this._closeMenus);

    // Voorkeuren en texturen alleen als er iemand is ingelogd: op het inlogscherm gaven ze een
    // 401, en een 401 meldt 'Uw sessie is verlopen' -- ook aan wie nog nooit was ingelogd.
    // Na het inloggen haalt _enterApp ze alsnog op.
    if (this.isAuthenticated) this._loadUserSettings();

    // Direct meebewegen als de gebruiker de voorkeur omzet, zonder herladen.
    this._prefsUpdatedHandler = (e) => {
      const v = e.detail?.[BREADCRUMBS_PREF];
      if (typeof v === 'boolean') this._showCrumbs = v;
      const f = e.detail?.[FANCY_BG_PREF];
      if (typeof f === 'boolean') this._fancyBg = f;
    };
    window.addEventListener('preferences-updated', this._prefsUpdatedHandler);

    // Refresh the sidebar avatar/name after the user edits their profile.
    this._profileUpdatedHandler = () => this._loadProfile();
    window.addEventListener('profile-updated', this._profileUpdatedHandler);

    /*
     * De bedrijvenkiezer.
     *
     * Word je aan een bedrijf toegevoegd -- door jezelf vanuit /admin/companies, of door een
     * beheerder van dat bedrijf -- dan verscheen dat bedrijf pas na uit- en inloggen in de
     * keuzelijst. Twee dingen ontbraken: de lijst werd wel bijgewerkt maar de topbalk hoorde
     * dat niet, en niemand vroeg de server ooit opnieuw wie je erbij hebt gekregen.
     */
    this._companies = auth.getCompanies();
    this._companiesChangedHandler = () => { this._companies = auth.getCompanies(); };
    window.addEventListener(COMPANIES_EVENT, this._companiesChangedHandler);

    // Terug op het tabblad is het natuurlijke moment om te kijken; de drempel houdt het bij
    // rap heen-en-weer klikken op één verzoek.
    this._companiesVisibilityHandler = () => {
      if (document.visibilityState === 'visible') this._maybeRefreshCompanies();
    };
    document.addEventListener('visibilitychange', this._companiesVisibilityHandler);
    window.addEventListener('focus', this._companiesVisibilityHandler);
    // En terwijl het tabblad gewoon openstaat, elke vijf minuten: iemand die jou toevoegt kan
    // je dat naast de applicatie om laten weten, en dan wil je niet eerst hoeven herladen.
    this._companiesTimer = setInterval(() => this._maybeRefreshCompanies(), 5 * 60_000);

    this._loadProfile();
    this._loadLicense();
  }

  /** Voorkeuren van de gebruiker en texturen van het bedrijf; bij opstarten en na inloggen. */
  _loadUserSettings() {
    // Recente zoektermen horen bij de gebruiker en komen dus van de server.
    api.getPreferences()
      .then((prefs) => {
        const r = prefs?.['search.recent']; if (Array.isArray(r)) this._recentSearches = r.slice(0, 2);
        // Alleen een expliciete `false` verbergt het pad: ontbreekt de voorkeur, dan staat hij aan.
        this._showCrumbs = prefs?.[BREADCRUMBS_PREF] !== false;
        // Standaard uit: alleen een expliciete `true` zet de sfeerfoto aan.
        this._fancyBg = prefs?.[FANCY_BG_PREF] === true;
      })
      .catch(() => { /* zonder geschiedenis werkt de zoekbalk gewoon */ });
    // Texturen van het bedrijf voor elke boorprofiel-tekening. Mislukt dit, dan tekent alles
    // gewoon volgens NEN 6693 Figuur 1.
    api.getCompanyTextures()
      .then((res) => setCompanyTextures(res?.overrides))
      .catch(() => {});
  }

  /** Bedrijvenlijst verversen, hooguit één keer per minuut. */
  _maybeRefreshCompanies() {
    if (!auth.isAuthenticated?.()) return;
    const nu = Date.now();
    if (nu - (this._companiesCheckedAt || 0) < 60_000) return;
    this._companiesCheckedAt = nu;
    auth.refreshCompanies().catch(() => { /* een mislukte verversing mag niets kapotmaken */ });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener(TEMPLATE_NAME_EVENT, this._onTemplateName);
    window.removeEventListener('auth-expired', this._authExpiredHandler);
    window.removeEventListener('popstate', this.routeChangeListener);
    document.removeEventListener('click', this._closeMenus);
    window.removeEventListener('profile-updated', this._profileUpdatedHandler);
    window.removeEventListener('preferences-updated', this._prefsUpdatedHandler);
    window.removeEventListener(COMPANIES_EVENT, this._companiesChangedHandler);
    document.removeEventListener('visibilitychange', this._companiesVisibilityHandler);
    window.removeEventListener('focus', this._companiesVisibilityHandler);
    clearInterval(this._companiesTimer);
  }

  onLoginSuccess() {
    this.sessionExpiredMessage = null;
    // Belongs to more than one company → pick one before entering the app.
    if (auth.getCompanies().length > 1) {
      this.needsCompanySelection = true;
      this.companySwitchError = null;
      return;
    }
    this._enterApp();
  }

  _enterApp() {
    this.needsCompanySelection = false;
    this.isAuthenticated = true;
    identifyUser(auth.getUser());
    setSentryUser(auth.getUser());
    capture('user_logged_in');
    // Seed the profile from the fresh login user, then refresh from the backend.
    const u = auth.getUser();
    if (u) this._profile = { firstName: u.first_name || '', lastName: u.last_name || '', email: u.email || '', avatarUpdatedAt: u.avatar_updated_at || null };
    this._loadProfile();
    // Opnieuw ophalen: een ander bedrijf kan een andere licentie hebben.
    this._companyLicense = this._licentieUitOpslag();
    this._betaFeatures = profielUitOpslag()?.betaFeatures ?? undefined;
    this._loadLicense();
    this._loadUserSettings();
    this.loadProjects();
  }

  async chooseCompany(companyId) {
    this.companySwitchError = null;
    const result = await auth.selectCompany(companyId);
    if (result.success) {
      this._enterApp();
    } else {
      this.companySwitchError = result.error || 'Bedrijf selecteren mislukt';
    }
  }

  /** Switch company from within the app (sidebar) — reload so all views re-fetch. */
  /**
   * Wijst de huidige pagina naar één specifiek ding van het HUIDIGE bedrijf?
   *
   * Alleen die pagina's zijn na het wisselen betekenisloos: het id bestaat niet in het andere
   * bedrijf. Pagina's als /team, /settings en /admin zijn wél bedrijfsgebonden maar niet
   * ding-gebonden — die laten na het herladen gewoon het nieuwe bedrijf zien, en daar iemand
   * weghalen zou onnodig zijn plek afpakken.
   */
  _isEntityScopedRoute() {
    const path = window.location.pathname.replace(/^\//, '');
    if (path.startsWith('projects/')) return true;
    // templates/new is een leeg formulier, geen bestaand sjabloon.
    if (path.startsWith('templates/') && path !== 'templates/new') return true;
    return false;
  }

  async switchCompany(companyId) {
    if (!companyId || companyId === auth.getActiveCompany()?.id) return;
    const result = await auth.selectCompany(companyId);
    if (result.success) {
      // Sta je op een project van het vorige bedrijf, dan bestaat dat hier niet en kreeg je
      // 'Project niet gevonden'. Terug naar de projectlijst dus — die klopt altijd.
      if (this._isEntityScopedRoute()) window.location.href = '/projects';
      else window.location.reload();
    } else {
      this.companySwitchError = result.error || 'Bedrijf wisselen mislukt';
    }
  }

  _roleLabel(role) {
    return role === 'owner' ? 'Eigenaar' : role === 'manager' ? 'Beheerder' : 'Lid';
  }

  /** Ben ik gast in het actieve bedrijf? Alleen voor wat de schil wel en niet toont. */
  get _isGuest() {
    return auth.getActiveCompany()?.role === 'guest';
  }

  /** A company owner/manager (or a sysadmin) — may edit Recorder App settings. */
  get _canManageRecorder() {
    const u = auth.getUser();
    return !!u && (u.is_system_admin || u.company_role === 'owner' || u.company_role === 'manager');
  }

  /** Waar 'Instellingen' heen gaat: de bedrijfsinstellingen voor een beheerder, anders de eigen. */
  get _settingsHome() {
    return this._canManageRecorder ? '/settings/recorder' : '/personal/settings';
  }

  /** Sjablonen zijn alleen zichtbaar voor beheerders/eigenaren (of een sysadmin). */
  get _canManageTemplates() {
    const u = auth.getUser();
    return !!u && (u.is_system_admin || u.company_role === 'owner' || u.company_role === 'manager');
  }

  _renderForbidden(message = 'Deze pagina is alleen voor systeembeheerders.') {
    return html`<div style="padding:3rem; text-align:center; color:var(--mv-text-secondary,#6c757d);">
      <h2 style="color:var(--mv-text);">Geen toegang</h2>
      <p>${message}</p>
    </div>`;
  }

  handleLogout() {
    auth.logout();
    resetAnalytics();
    clearSentryUser();
    this.isAuthenticated = false;
    this.currentView = 'projects';
    this.projects = [];
  }

  handleAuthExpired(message) {
    this.isAuthenticated = false;
    this.currentView = 'projects';
    this.projects = [];
    this.sessionExpiredMessage = message || 'Uw sessie is verlopen. Log opnieuw in.';
  }

  /** Balk breed of smal. Blijft staan tussen sessies: het is een voorkeur, geen toestand. */
  _toggleNav() {
    this._navOpen = !this._navOpen;
    localStorage.setItem('mv_nav_open', this._navOpen ? '1' : '0');
  }

  toggleTheme() {
    this.isDarkMode = !this.isDarkMode;
    localStorage.setItem('mv_theme', this.isDarkMode ? 'dark' : 'light');
    this.applyTheme();
  }

  /** Explicitly set light/dark (used by the sun/moon toggle in the profile menu). */
  setTheme(dark) {
    if (this.isDarkMode === dark) return;
    this.toggleTheme();
  }

  /**
   * Zoeken terwijl je typt, maar niet bij elke toetsaanslag.
   *
   * 220 ms wachten na de laatste toets: snel genoeg om als 'meteen' te voelen, en het scheelt
   * een verzoek per letter. Onder de twee tekens zoeken we niet -- dat levert alles op en
   * niets bruikbaars.
   */
  _onSearchInput(v) {
    this._searchQ = v;
    this._searchOpen = true;
    clearTimeout(this._searchTimer);
    if (v.trim().length < 2) { this._searchGroups = []; this._searchBusy = false; return; }
    this._searchBusy = true;
    this._searchTimer = setTimeout(async () => {
      const asked = v;
      try {
        const res = await api.search(v);
        // Antwoorden kunnen door elkaar terugkomen; alleen het antwoord op de HUIDIGE vraag
        // tonen, anders knippert een ouder resultaat er overheen.
        if (this._searchQ !== asked) return;
        this._searchGroups = res?.groups || [];
      } catch (_) {
        this._searchGroups = [];
      } finally {
        if (this._searchQ === asked) this._searchBusy = false;
      }
    }, 220);
  }

  /**
   * Bewaar de laatste twee zoektermen -- maar alleen als er ook echt op een resultaat is
   * geklikt.
   *
   * Wie typt, niets vindt en het venster sluit, heeft geen bruikbare term achtergelaten; die
   * onthouden zou de lijst vullen met tikfouten en halve woorden. Pas een klik bewijst dat de
   * term ergens toe leidde.
   *
   * Serverseidig bij de gebruiker, net als de kolomkeuze: iemand die op kantoor zoekt en de
   * volgende dag op een andere machine zit, verwacht zijn eigen geschiedenis terug. Schrijven
   * gebeurt alleen bij een klik, dus dat is hooguit een paar verzoeken per dag.
   */
  async _rememberSearch(term) {
    const q = (term || '').trim();
    if (!q) return;
    const next = [q, ...this._recentSearches.filter((t) => t.toLowerCase() !== q.toLowerCase())].slice(0, 2);
    this._recentSearches = next;
    try { await api.setPreference('search.recent', next); } catch (_) { /* comfort, geen voorwaarde */ }
  }

  _openSearchResult(url) {
    this._rememberSearch(this._searchQ);
    this._searchOpen = false;
    this._searchQ = '';
    this._searchGroups = [];
    this.navigate(url);
  }

  _renderSearch() {
    const hasResults = this._searchGroups.some((g) => g.items.length);
    const typing = this._searchQ.trim().length >= 2;
    return html`
      <div class="search" @click=${(e) => e.stopPropagation()}>
        <span class="search-icon">${unsafeSVG(lucideIcon(Search, 15))}</span>
        <input class="search-input" type="text" placeholder="Zoek in GeoVoice" .value=${this._searchQ}
          @input=${(e) => this._onSearchInput(e.target.value)}
          @focus=${() => { this._searchOpen = true; }}
          @keydown=${(e) => { if (e.key === 'Escape') { this._searchOpen = false; e.target.blur(); } }}>

        ${this._searchOpen && !typing && this._recentSearches.length ? html`
          <div class="search-menu">
            <div class="search-group">Recente zoekwoorden</div>
            ${this._recentSearches.map((t) => html`
              <button class="search-hit" @click=${() => { this._onSearchInput(t); this.renderRoot.querySelector('.search-input')?.focus(); }}>
                <span class="t">${t}</span>
              </button>`)}
          </div>` : ''}

        ${this._searchOpen && typing ? html`
          <div class="search-menu">
            ${!hasResults ? html`
              <div class="search-empty">
                ${this._searchBusy ? 'Zoeken…' : `Niets gevonden voor "${this._searchQ.trim()}"`}
              </div>
            ` : this._searchGroups.map((g) => html`
              <div class="search-group">${g.label}</div>
              ${g.items.map((it) => html`
                <button class="search-hit" @click=${() => this._openSearchResult(it.url)}>
                  <span class="t">${it.title}</span>
                  ${it.subtitle ? html`<span class="s">${it.subtitle}</span>` : ''}
                </button>`)}
            `)}
          </div>` : ''}
      </div>`;
  }

  /**
   * Het pad naar de huidige pagina, als lijst van { label, href }.
   *
   * De laatste kruimel is waar je BENT en krijgt geen href: een link naar de pagina waar je
   * al staat doet niets en nodigt uit tot een klik die niets oplevert.
   *
   * Namen die pas na het laden bekend zijn -- een project, een codelijst -- vallen terug op
   * een neutrale term tot ze binnen zijn, zodat het pad niet leeg blijft of verspringt.
   */
  _breadcrumbs() {
    const v = this.currentView;
    const project = () => (this.projects || []).find((p) => p.id === this.selectedProjectId);
    const pid = this.selectedProjectId;
    const tab = this.projectTab || 'overzicht';

    /*
     * Zusterpagina's van de laatste kruimel. Daarmee wordt het pad ook een keuze: je ziet
     * niet alleen waar je bent, maar stapt met één klik naar iets op hetzelfde niveau.
     */
    const projectTabs = pid ? [
      { label: 'Overzicht', href: `/projects/${pid}` },
      { label: 'Boringen', href: `/projects/${pid}/borings` },
      { label: 'Kaart', href: `/projects/${pid}/map` },
      { label: 'Labopdrachten', href: `/projects/${pid}/labopdrachten` },
      { label: 'Export', href: `/projects/${pid}/export` },
    ] : [];
    // Hoofdniveau: hiermee is ook /projects een pad waar je iets aan hebt -- je springt er
    // naar Team of de Recorder App zonder eerst naar de balk links te hoeven.
    const mainPages = [
      { label: 'Projecten', href: '/projects' },
      ...(this._hasGeoDesk ? [{ label: 'Labopdrachten', href: '/labopdrachten' }] : []),
      { label: 'Team', href: '/team' },
      { label: 'Recorder App', href: '/recorder' },
      ...(this._canManageRecorder ? [{ label: 'Instellingen', href: '/settings/recorder' }] : []),
    ];
    const settingsPages = [
      ...(this._canManageRecorder ? [
        { label: 'Bedrijf', href: '/settings/company' },
        { label: 'Recorder app', href: '/settings/recorder' },
        { label: 'Boringsjablonen', href: '/templates' },
        ...(this._hasReportBeta ? [{ label: 'Rapportagesjablonen', href: '/settings/report' }] : []),
        { label: 'Texturen', href: '/settings/textures' },
      ] : []),
      { label: 'Persoonlijk', href: '/personal/settings' },
      { label: 'Opmaak', href: '/personal/appearance' },
    ];
    const adminPages = [
      { label: 'Gebruikers', href: '/admin/users' },
      { label: 'Bedrijven', href: '/admin/companies' },
      { label: 'Analyse', href: '/admin/analysis' },
      { label: 'Valideer model', href: '/admin/validation' },
      { label: 'Instellingen', href: '/admin/settings/recorder' },
    ];

    const trails = {
      'projects': [{ label: 'Projecten', siblings: mainPages }],
      'project-detail': this._projectTrail(project(), tab, projectTabs),
      'lab-orders': [{ label: 'Labopdrachten', siblings: mainPages }],
      // De opdracht zelf zet zijn nummer erachter (zie hieronder); het niveau erboven hangt
      // af van waar je vandaan kwam, net als de pijl terug op de pagina zelf.
      'lab-order': this.labOrderReturn === 'alle'
        ? [{ label: 'Labopdrachten', href: '/labopdrachten', siblings: mainPages }]
        : [{ label: 'Projecten', href: '/projects', siblings: mainPages }, { label: 'Labopdracht' }],
      'team': [{ label: 'Team', siblings: mainPages }],
      'recorder': [{ label: 'Recorder App', siblings: mainPages }],
      'recorder-settings': [{ label: 'Instellingen', href: '/settings/recorder' }, { label: 'Recorder app', siblings: settingsPages }],
      'company-settings': [{ label: 'Instellingen', href: '/settings/recorder' }, { label: 'Bedrijf', siblings: settingsPages }],
      'texture-settings': [{ label: 'Instellingen', href: '/settings/recorder' }, { label: 'Bedrijf', href: '/settings/company' }, { label: 'Texturen', siblings: settingsPages }],
      'personal-settings': [{ label: 'Instellingen', href: this._settingsHome }, { label: 'Persoonlijk', siblings: settingsPages }],
      'personal-appearance': [{ label: 'Instellingen', href: this._settingsHome }, { label: 'Opmaak', siblings: settingsPages }],
      'templates': [{ label: 'Instellingen', href: '/settings/recorder' }, { label: 'Boringsjablonen', siblings: settingsPages }],
      'report-settings': [{ label: 'Instellingen', href: '/settings/recorder' }, { label: 'Rapportagesjablonen', siblings: settingsPages }],
      'template-editor': [{ label: 'Instellingen', href: '/settings/recorder' }, { label: 'Boringsjablonen', href: '/templates' },
        { label: this._templateName || (this.getRouteTemplateId() && this.getRouteTemplateId() !== 'new' ? 'Bewerken' : 'Nieuw sjabloon') }],
      'users': [{ label: 'Beheer' }, { label: 'Gebruikers', siblings: adminPages }],
      'bedrijven': [{ label: 'Beheer' }, { label: 'Bedrijven', siblings: adminPages }],
      'analysis': [{ label: 'Beheer' }, { label: 'Analyse', siblings: adminPages }],
      'validation': [{ label: 'Beheer' }, { label: 'Valideer model', href: '/admin/validation' }],
      'validation-model-editor': [{ label: 'Beheer' }, { label: 'Valideer model', href: '/admin/validation' }, { label: 'Model' }],
      'admin-settings-recorder': [{ label: 'Beheer' }, { label: 'Instellingen', siblings: adminPages }],
      'admin-settings-model': [{ label: 'Beheer' }, { label: 'Instellingen', href: '/admin/settings/recorder' }, { label: 'AI' }],
      'admin-settings-required-fields': [{ label: 'Beheer' }, { label: 'Instellingen', href: '/admin/settings/recorder' }, { label: 'Verplichte velden' }],
      'admin-settings-codelists': [{ label: 'Beheer' }, { label: 'Instellingen', href: '/admin/settings/recorder' }, { label: 'Codelijsten', href: '/admin/settings/codelists' }],
    };
    const trail = trails[v] ? [...trails[v]] : null;
    if (!trail) return [];

    // Subpagina's met een eigen URL krijgen hun naam als laatste kruimel -- de LEESBARE naam
    // uit de registry, niet het id uit de URL. 'lagen-nen5104' in een kruimelpad zegt niemand
    // iets; kunnen we de naam niet vinden, dan laten we de kruimel liever weg.
    if (v === 'validation' && this.validationProfile) {
      const label = EXTRACTION_PROFILES.find((x) => x.id === this.validationProfile)?.label;
      if (label) {
        trail.push({ label, siblings: EXTRACTION_PROFILES.map((x) => ({ label: x.label, href: `/admin/validation/${x.id}` })) });
      }
    }
    if (v === 'admin-settings-codelists' && this.codelistDomain) {
      const domains = getCodelistDomains();
      const label = domains.find((d) => d.id === this.codelistDomain)?.name;
      if (label) {
        trail.push({ label, siblings: domains.map((d) => ({ label: d.name, href: `/admin/settings/codelists/${d.id}` })) });
      }
    }

    // De laatste is waar je bent: nooit een link.
    if (trail.length) delete trail[trail.length - 1].href;
    return trail;
  }

  /**
   * Kruimels van een project, inclusief het hoofdproject als dit een deelproject is.
   *
   * Een deelproject hangt onder een project, en dat hoorde je in het pad terug te zien in
   * plaats van in een tweede kruimelbalk op de pagina zelf. Het niveau 'Deelprojecten' wijst
   * naar de pagina van het hoofdproject -- daar staat de lijst.
   *
   * Zusters zijn altijd projecten met DEZELFDE ouder: van een deelproject wissel je naar een
   * ander deelproject, van een hoofdproject naar een ander hoofdproject. Wisselen tussen die
   * twee niveaus is navigeren, geen wisselen.
   */
  _projectTrail(project, tab, projectTabs) {
    const all = this.projects || [];
    const parent = project?.parent_id ? all.find((x) => x.id === project.parent_id) : null;
    const nameOf = (x) => x?.project_name || x?.name || 'Naamloos';
    const linkFor = (id) => (tab === 'overzicht' ? `/projects/${id}` : `/projects/${id}/${tab}`);

    const siblings = all
      .filter((x) => (x.parent_id || null) === (project?.parent_id || null))
      .map((x) => ({ label: nameOf(x), href: linkFor(x.id) }))
      .sort((a, b) => a.label.localeCompare(b.label, 'nl', { numeric: true }));

    const trail = [{ label: 'Projecten', href: '/projects' }];
    if (parent) {
      trail.push({ label: nameOf(parent), href: `/projects/${parent.id}` });
      trail.push({ label: 'Deelprojecten', href: `/projects/${parent.id}` });
    }
    trail.push({
      label: project ? nameOf(project) : 'Project',
      href: `/projects/${this.selectedProjectId}`,
      siblings,
    });
    const kruimelTab = tab === 'opdracht' ? 'vooronderzoek' : tab;
    trail.push({
      label: { overzicht: 'Overzicht', vooronderzoek: 'Vooronderzoek', borings: 'Boringen', map: 'Kaart', labopdrachten: 'Labopdrachten', rapportage: 'Rapportage', export: 'Export' }[kruimelTab],
      ...(kruimelTab === 'vooronderzoek' ? { href: `/projects/${this.selectedProjectId}/vooronderzoek` } : {}),
      siblings: projectTabs,
    });
    // Vooronderzoek heeft twee subtabbladen (?deel=monsterplan, anders de veldwerkopdracht).
    if (kruimelTab === 'vooronderzoek') {
      const deel = tab !== 'opdracht' && new URLSearchParams(window.location.search).get('deel') === 'monsterplan'
        ? 'Monsterplan' : 'Veldwerkopdracht';
      trail.push({ label: deel });
    }
    return trail;
  }

  _renderCrumbs() {
    if (this._showCrumbs === false) return '';
    const trail = this._breadcrumbs();
    // Eén kruimel zonder keuzes is geen pad; dan zegt de balk links het al. Mét zusterpagina's
    // is het wél nuttig: dan is het geen wegwijzer maar een schakelaar.
    if (!trail.length) return '';
    if (trail.length < 2 && !(trail[0].siblings?.length > 1)) return '';
    return html`
      <div class="crumbs">
        ${trail.map((c, i) => html`
          ${i > 0 ? html`<span class="sep">${unsafeSVG(lucideIcon(ChevronRight, 13))}</span>` : ''}
          ${this._renderCrumb(c, i)}
        `)}
      </div>`;
  }

  /**
   * Eén kruimel. Elk niveau met zusters krijgt een keuzelijst, niet alleen de laatste: op een
   * projectpagina wil je ook van PROJECT kunnen wisselen, en dat niveau is nooit het laatste.
   *
   * Naam en pijltje doen bewust iets anders: de naam brengt je naar dat niveau, het pijltje
   * laat zien wat er naast ligt. Zou de hele kruimel het menu openen, dan kun je niet meer
   * doorklikken naar het project zelf.
   */
  _renderCrumb(c, i) {
    const hasMenu = c.siblings?.length > 1;
    const label = c.href
      ? html`<a href=${c.href} @click=${this.handleLinkClick}>${c.label}</a>`
      : html`<span class="current">${c.label}</span>`;
    if (!hasMenu) return label;

    const here = window.location.pathname;
    return html`
      <span style="position:relative; display:inline-flex; align-items:center; gap:2px; min-width:0;"
        @click=${(e) => e.stopPropagation()}>
        ${label}
        <button class="crumb-caret" title="Wissel" aria-haspopup="true"
          aria-expanded=${this._crumbMenuOpen === i ? 'true' : 'false'}
          @click=${() => { this._crumbMenuOpen = this._crumbMenuOpen === i ? null : i; }}>
          ${unsafeSVG(lucideIcon(ChevronDown, 12))}
        </button>
        ${this._crumbMenuOpen === i ? html`
          <div class="crumb-menu">
            ${c.siblings.map((o) => html`
              <a href=${o.href} class=${o.href === here ? 'on' : ''}
                @click=${(e) => { this._crumbMenuOpen = null; this.handleLinkClick(e); }}>${o.label}</a>`)}
          </div>` : ''}
      </span>`;
  }



  /**
   * Topbalk: bedrijf links, omgeving en gebruiker rechts.
   *
   * Alles wat over JOU en je CONTEXT gaat staat hier; de balk links gaat alleen over waar je
   * heen navigeert. Daardoor blijft de rail smal genoeg om alleen iconen te tonen.
   */
  _renderTopbar() {
    const companies = this._companies || auth.getCompanies();
    // Bedrijven waar je alleen mag meekijken. Niet als reactieve eigenschap bijgehouden: het
    // menu wordt toch opnieuw getekend zodra je het opent, dus de lijst is dan altijd vers.
    const lookAlong = auth.getLookAlongCompanies();
    const active = auth.getActiveCompany();
    const readOnly = auth.isLookAlongSession();
    const multi = (companies.length + lookAlong.length) > 1;
    return html`
      <div class="topbar">
        <div style="position:relative;" @click=${(e) => e.stopPropagation()}>
          <button @click=${() => { if (multi) this.companyMenuOpen = !this.companyMenuOpen; }}
            title=${multi ? 'Wissel van bedrijf' : (active?.name || '')}
            style="display:flex; align-items:center; gap:9px; padding:6px 11px; border-radius:9px; border:1px solid var(--mv-border); background:var(--mv-surface); cursor:${multi ? 'pointer' : 'default'}; min-width:190px; max-width:300px;">
            <span style="flex-shrink:0; width:26px; height:26px; border-radius:7px; background:var(--mv-primary); color:#fff; display:flex; align-items:center; justify-content:center; font-weight:800; font-size:0.8rem;">${(active?.name || '?').charAt(0).toUpperCase()}</span>
            <span style="flex:1; text-align:left; font-weight:700; font-size:0.86rem; color:var(--mv-text); white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${active?.name || ''}</span>
            ${readOnly ? html`
              <span style="flex-shrink:0; padding:2px 7px; border-radius:6px; background:var(--mv-surface-alt,#f8f9fa); border:1px solid var(--mv-border); color:var(--mv-text-sub,#6c757d); font-size:0.66rem; font-weight:800; letter-spacing:0.06em;">LEZEN</span>` : ''}
            ${multi ? html`<span style="flex-shrink:0; color:var(--mv-text-sub,#6c757d); display:flex;">${unsafeSVG(lucideIcon(ChevronsUpDown, 15))}</span>` : ''}
          </button>
          ${this.companyMenuOpen ? html`
            <div style="position:absolute; left:0; top:calc(100% + 6px); min-width:100%; width:max-content; max-width:340px; background:var(--mv-surface,#fff); border:1px solid var(--mv-border); border-radius:10px; box-shadow:0 8px 24px rgba(0,0,0,0.15); z-index:var(--mv-z-overlay); padding:4px; max-height:320px; overflow-y:auto;">
              ${companies.map((c) => html`
                <button @click=${() => { this.companyMenuOpen = false; this.switchCompany(c.id); }}
                  style="display:flex; align-items:center; gap:10px; width:100%; padding:8px; border:none; background:${c.id === active?.id ? 'var(--mv-surface-alt,#f8f9fa)' : 'none'}; cursor:pointer; border-radius:8px; text-align:left;">
                  <span style="flex-shrink:0; width:26px; height:26px; border-radius:6px; background:var(--mv-primary); color:#fff; display:flex; align-items:center; justify-content:center; font-weight:800; font-size:0.8rem;">${(c.name || '?').charAt(0).toUpperCase()}</span>
                  <span style="flex:1; font-weight:600; font-size:0.85rem; color:var(--mv-text);">${c.name}</span>
                  <!-- Je rol bij dit bedrijf, in dezelfde vorm als het LEZEN-label bij meekijken:
                       je hebt bij elk bedrijf een andere pet op, en dat wil je zien vóór je
                       wisselt. -->
                  <span style="flex-shrink:0; color:var(--mv-text-sub,#6c757d); font-size:0.66rem; font-weight:800; letter-spacing:0.06em;">${ROLE_TAG[c.role] || ''}</span>
                  ${c.id === active?.id ? html`<span style="color:var(--mv-primary); display:flex;">${unsafeSVG(lucideIcon(Check, 16))}</span>` : ''}
                </button>`)}

              <!-- Bedrijven die systeembeheerders hebben toegestaan mee te kijken. Apart en met
                   een eigen kopje: dit zijn géén bedrijven waar je bij hoort, en je kunt er
                   niets wijzigen. Elke systeembeheerder ziet dezelfde lijst. -->
              ${lookAlong.length ? html`
                <div style="margin-top:4px; padding:8px 8px 4px; border-top:1px solid var(--mv-border); font-size:0.66rem; font-weight:800; letter-spacing:0.07em; text-transform:uppercase; color:var(--mv-text-sub,#6c757d);">
                  Sysadmin leesrechten
                </div>
                ${lookAlong.map((c) => html`
                  <button @click=${() => { this.companyMenuOpen = false; this.switchCompany(c.id); }}
                    style="display:flex; align-items:center; gap:10px; width:100%; padding:8px; border:none; background:${c.id === active?.id ? 'var(--mv-surface-alt,#f8f9fa)' : 'none'}; cursor:pointer; border-radius:8px; text-align:left;">
                    <span style="flex-shrink:0; width:26px; height:26px; border-radius:6px; background:var(--mv-text-sub,#6c757d); color:#fff; display:flex; align-items:center; justify-content:center; font-weight:800; font-size:0.8rem;">${(c.name || '?').charAt(0).toUpperCase()}</span>
                    <span style="flex:1; font-weight:600; font-size:0.85rem; color:var(--mv-text);">${c.name}</span>
                    ${c.id === active?.id
                      ? html`<span style="color:var(--mv-primary); display:flex;">${unsafeSVG(lucideIcon(Check, 16))}</span>`
                      : html`<span style="flex-shrink:0; color:var(--mv-text-sub,#6c757d); font-size:0.66rem; font-weight:800; letter-spacing:0.06em;">LEZEN</span>`}
                  </button>`)}
              ` : ''}
            </div>` : ''}
        </div>

        ${this._renderSearch()}

        ${!CONFIG.IS_PRODUCTION ? html`
          <!-- Naast het bedrijf, want het hoort bij 'waar zit ik'. De branchnaam staat alleen
               in de tooltip: die is zelden nodig en kost anders permanent ruimte. -->
          <span @mousemove=${tipMove} @mouseleave=${tipHide}
            data-tip=${`${CONFIG.BRANCH ? `Branch: ${CONFIG.BRANCH}` : ''}`}
            style="flex-shrink:0; display:inline-flex; align-items:center; padding:4px 9px; border-radius:7px; background:var(--mv-danger,#f1416c); color:#fff; font-size:10px; font-weight:800; letter-spacing:1px; cursor:default;">
            ${CONFIG.ENV_LABEL}
          </span>` : ''}

        <span class="spacer"></span>

        <!-- Licht/donker staat hier en niet meer in het profielmenu: het is iets wat je
             omzet omdat je op dat moment te weinig of te veel licht hebt, en dan wil je niet
             eerst twee keer klikken om erbij te komen. -->
        <div class="theme-switch" role="group" aria-label="Thema">
          <button class=${this.isDarkMode ? '' : 'on'} @click=${() => this.setTheme(false)}
            data-tip="Licht" @mousemove=${tipMove} @mouseleave=${tipHide} aria-pressed=${!this.isDarkMode}>
            ${unsafeSVG(lucideIcon(Sun, 16))}
          </button>
          <button class=${this.isDarkMode ? 'on' : ''} @click=${() => this.setTheme(true)}
            data-tip="Donker" @mousemove=${tipMove} @mouseleave=${tipHide} aria-pressed=${this.isDarkMode}>
            ${unsafeSVG(lucideIcon(Moon, 16))}
          </button>
        </div>

        <div style="position:relative;" @click=${(e) => e.stopPropagation()}>
          <button @click=${() => { this.profileMenuOpen = !this.profileMenuOpen; }} title="Profielmenu"
            style="display:flex; align-items:center; padding:2px; border:none; background:none; cursor:pointer; border-radius:50%;">
            ${this._renderAvatar(32)}
          </button>
          ${this.profileMenuOpen ? this._renderProfileMenu() : ''}
        </div>
      </div>`;
  }

  /** Circular avatar: the uploaded image when set, else coloured initials. */
  _renderAvatar(size = 34, fontSize = '0.85rem') {
    const p = this._profile || { email: auth.getUser()?.email || '' };
    const url = this._profile?.avatarUpdatedAt ? api.avatarUrl(auth.getUser()?.id, this._profile.avatarUpdatedAt) : null;
    const box = `flex-shrink:0; width:${size}px; height:${size}px; border-radius:50%; overflow:hidden; display:flex; align-items:center; justify-content:center;`;
    if (url) {
      return html`<span style="${box} background:var(--mv-bg,#e9ecef);"><img src=${url} alt="" style="width:100%; height:100%; object-fit:cover;" /></span>`;
    }
    return html`<span style="${box} background:${avatarColor(p.email || displayName(p))}; color:#fff; font-weight:800; font-size:${fontSize};">${initials(p)}</span>`;
  }

  /** Pop-up profile menu (opens above the avatar): settings, logout. Thema staat in de
   *  topbalk zelf — zie _renderTopbar. */
  _renderProfileMenu() {
    const item = 'display:flex; align-items:center; gap:11px; width:100%; padding:11px 14px; border:none; background:none; cursor:pointer; text-align:left; font-size:0.88rem; color:var(--mv-text); font-family:var(--mv-font);';
    return html`
      <div style="position:absolute; right:0; top:calc(100% + 8px); width:270px; background:var(--mv-surface,#fff); border:1px solid var(--mv-border); border-radius:12px; box-shadow:0 10px 34px rgba(0,0,0,0.18); z-index:var(--mv-z-overlay); overflow:hidden;">
        <div style="display:flex; align-items:center; gap:11px; padding:13px 14px; border-bottom:1px solid var(--mv-border);">
          ${this._renderAvatar(38)}
          <div style="min-width:0;">
            <div style="font-weight:700; font-size:0.9rem; color:var(--mv-text); white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${displayName(this._profile) || 'Gebruiker'}</div>
            <div style="font-size:0.78rem; color:var(--mv-text-sub,#6c757d); white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${this._profile?.email || ''}</div>
          </div>
        </div>
        <button @click=${() => { this.profileMenuOpen = false; this.navigate('/personal/settings'); }} style=${item}
          @mouseenter=${(e) => e.currentTarget.style.background = 'var(--mv-bg,#f8f9fa)'} @mouseleave=${(e) => e.currentTarget.style.background = 'none'}>
          <span style="display:flex; color:var(--mv-text-sub,#6c757d);">${unsafeSVG(lucideIcon(Settings2, 17))}</span> Instellingen
        </button>
        <button @click=${this.handleLogout} style="${item} border-top:1px solid var(--mv-border); color:var(--mv-danger,#dc3545);"
          @mouseenter=${(e) => e.currentTarget.style.background = 'var(--mv-bg,#f8f9fa)'} @mouseleave=${(e) => e.currentTarget.style.background = 'none'}>
          <span style="display:flex;">${unsafeSVG(lucideIcon(LogOut, 17))}</span> Uitloggen
        </button>
      </div>
    `;
  }

  applyTheme() {
    if (this.isDarkMode) {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
  }

  handleRouteChange() {
    this.currentView = this.getRouteView();
    this.selectedProjectId = this.getRouteProjectId();
    this.validationProfile = this.getRouteValidationProfile();
    this.codelistDomain = this.getRouteCodelistDomain();
    this.projectTab = this.getRouteProjectTab();
    this.projectFocus = this.getRouteProjectFocus();
    this.labOrderId = this.getRouteLabOrderId();
    this.labOrderReturn = this.getRouteLabOrderReturn();
    // Een wissel die alleen de query raakt (Vooronderzoek: ?deel=monsterplan) verandert geen
    // eigenschap hierboven; zonder dit blijft de kruimel op het vorige subtabblad staan.
    this.requestUpdate();

    // Ook op een projectpagina: de kruimel toont dan de echte naam in plaats van 'Project',
    // en de keuzelijst kan naar een ander project wisselen.
    if (this.isAuthenticated && ['projects', 'project-detail'].includes(this.currentView)) {
      this.loadProjects();
    }
  }

  navigate(path) {
    if (!path) return;
    window.history.pushState({}, '', path);
    this.handleRouteChange();
  }

  handleLinkClick(e) {
    if (e.button !== 0 || e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    const href = e.currentTarget.getAttribute('href');
    this.navigate(href);
  }

  async loadProjects() {
    this.loading = true;
    this.error = null;

    try {
      this.projects = await api.getProjects();
    } catch (error) {
      console.error('Failed to load projects:', error);
      this.error = `Fout bij laden van projecten: ${error.message}`;
    } finally {
      this.loading = false;
    }
  }

  navigateToProject(projectId) {
    this.navigate(`/projects/${projectId}`);
  }

  navigateToProjects() {
    this.navigate(`/projects`);
  }

  _onCreateDeelproject(e) {
    const parentId = e.detail.parentId;
    this.currentView = 'projects';
    this.initialParentId = parentId;
    window.history.pushState({}, '', '/projects');
  }

  onProjectCreated() {
    this.initialParentId = null;
    this.loadProjects();
  }

  onProjectDeleted() {
    this.navigateToProjects();
  }

  _onEditProject(e) {
    this.projectToEdit = e.detail.project;
    this.currentView = 'projects';
    window.history.pushState({}, '', '/projects');
    this.handleRouteChange();
  }

  async _onDeleteProject(e) {
    const confirmed = await confirmDialog({
      title: 'Project verwijderen',
      message: 'Weet u zeker dat u dit project wilt verwijderen?',
      confirmLabel: 'Verwijderen',
      danger: true,
    });
    if (!confirmed) return;
    try {
      await api.deleteProject(e.detail.projectId);

      // If we deleted the project we are currently viewing
      if (this.selectedProjectId === e.detail.projectId) {
        this.navigateToProjects();
      } else {
        // If we deleted a sub-project while viewing the parent,
        // we need to tell the detail view to refresh.
        // Since selectedProjectId hasn't changed, we trigger a refresh manually.
        this.loadProjects();
        this.handleRouteChange();

        // Force ProjectDetail to reload its specific data if it's the current view
        const detailView = this.shadowRoot.querySelector('project-detail');
        if (detailView) {
          detailView.loadProject();
        }
      }
    } catch (err) {
      alertDialog({ message: 'Verwijderen mislukt: ' + err.message });
    }
  }

  /** Wikkel een instellingen-pagina in het gedeelde submenu (links) + content (rechts). */
  _settingsShell(active, content) {
    return html`
      <div class="settings-shell">
        <div class="settings-aside">
          <settings-nav .active=${active} .canCompany=${this._canManageRecorder} .canTemplates=${this._canManageTemplates} .canReport=${this._hasReportBeta} @navigate=${(e) => this.navigate(e.detail.path)}></settings-nav>
        </div>
        <div style="flex:1; min-width:0;">${content}</div>
      </div>
    `;
  }

  renderCurrentView() {
    if (this.loading) {
      return html`
        <div class="loading-container">
          <sl-spinner></sl-spinner>
          <p>Projecten worden geladen...</p>
        </div>
      `;
    }

    if (this.error) {
      return html`
        <div class="error">
          ${this.error}
          <br><br>
          <sl-button variant="primary" @click=${this.loadProjects}>
            Opnieuw proberen
          </sl-button>
        </div>
      `;
    }

    // Single gate for the entire /admin/* namespace (also on direct URL entry).
    if (window.location.pathname.startsWith('/admin/')) {
      if (!auth.isSystemAdmin() || this.currentView === 'admin-unknown') {
        return this._renderForbidden();
      }
    }

    // De bedrijfsinstellingen zijn alleen voor een bedrijfsbeheerder. Een gewone gebruiker die er
    // via een link of de adresbalk komt, gaat naar zijn eigen instellingen.
    if (COMPANY_SETTINGS_VIEWS.includes(this.currentView) && !this._canManageRecorder) {
      queueMicrotask(() => { window.history.replaceState({}, '', '/personal/settings'); this.handleRouteChange(); });
      return '';
    }

    // Sjablonen: alleen beheerders/eigenaren (ook bij directe URL).
    if ((this.currentView === 'templates' || this.currentView === 'template-editor') && !this._canManageTemplates) {
      return this._renderForbidden('Deze pagina is alleen voor beheerders en eigenaren.');
    }

    switch (this.currentView) {
      case 'project-detail':
        return html`
          <project-detail 
            .projectId=${this.selectedProjectId}
            .activeTab=${this.projectTab || 'overzicht'}
            .deepLink=${this.projectFocus}
            @project-selected=${(e) => this.navigateToProject(e.detail.projectId)}
            @back-to-projects=${this.navigateToProjects}
            @project-updated=${this.onProjectUpdated}
            @project-deleted=${this.onProjectDeleted}
            @edit-project=${(e) => this._onEditProject(e)}
            @delete-project=${(e) => this._onDeleteProject(e)}
          ></project-detail>
        `;
      // Labopdrachten alleen met GeoDesk. Zonder: niet de pagina met overal 403's, maar één melding.
      case 'lab-orders':
        if (this._companyLicense && !this._hasGeoDesk) return this._renderForbidden('Labopdrachten horen bij GeoDesk. Dit bedrijf heeft geen GeoDesk-licentie.');
        return html`<lab-koppeling-page></lab-koppeling-page>`;
      case 'lab-order':
        if (this._companyLicense && !this._hasGeoDesk) return this._renderForbidden('Labopdrachten horen bij GeoDesk. Dit bedrijf heeft geen GeoDesk-licentie.');
        return html`<lab-order-page .orderId=${this.labOrderId} .terug=${this.labOrderReturn}></lab-order-page>`;
      case 'users':
        return html`<admin-users></admin-users>`;
      case 'bedrijven':
        return html`<company-dashboard></company-dashboard>`;
      case 'analysis':
        return html`<analysis-page></analysis-page>`;
      case 'validation':
        return html`<validation-page .profile=${this.validationProfile}></validation-page>`;
      case 'validation-model-editor':
        return html`<validation-model-editor .versionId=${this.getRouteModelId()}></validation-model-editor>`;
      case 'team':
        // Een gast die de URL intypt: terug naar zijn projecten in plaats van een pagina die
        // alleen een 403 van de backend kan tonen.
        if (this._isGuest) { window.location.replace('/projects'); return ''; }
        return html`<team-page></team-page>`;
      case 'personal-settings':
        return this._settingsShell('personal-settings', html`<personal-settings></personal-settings>`);
      case 'personal-appearance':
        return this._settingsShell('personal-appearance', html`<personal-appearance></personal-appearance>`);
      case 'templates':
        return this._settingsShell('templates', html`<templates-page></templates-page>`);
      case 'template-editor':
        return this._settingsShell('template-editor', html`<template-editor .templateId=${this.getRouteTemplateId() || 'new'}></template-editor>`);
      case 'recorder':
        return html`<recorder-app></recorder-app>`;
      case 'recorder-settings':
        return this._settingsShell('recorder-settings', html`<recorder-settings></recorder-settings>`);
      case 'company-settings':
        return this._settingsShell('company-settings', html`<company-settings></company-settings>`);
      case 'report-settings':
        // Bètafunctie Rapportage; de endpoints zijn zelf ook dicht (requireBetaFeature).
        // Pas tonen als bekend is welke bètafuncties aan staan; anders flitst de pagina even.
        if (this._betaFeatures == null) return '';
        if (!this._hasReportBeta) return this._renderForbidden('Rapportage staat voor dit bedrijf niet aan.');
        return this._settingsShell('report-settings', html`<report-settings-page ?canEdit=${this._canManageTemplates}></report-settings-page>`);
      case 'texture-settings':
        // Een gast (alleen kijkrechten) hoort hier niet; de URL intypen brengt hem terug naar
        // zijn projecten. De server geeft hem het voorbeeld ook niet (CompanyTexturesController).
        if (this._isGuest) { window.location.replace('/projects'); return ''; }
        return this._settingsShell('texture-settings', html`<texture-settings></texture-settings>`);
      case 'admin-settings-recorder':
        return html`<admin-global-settings tab="recorder"></admin-global-settings>`;
      case 'admin-settings-model':
        return html`<admin-global-settings tab="model"></admin-global-settings>`;
      case 'admin-settings-required-fields':
        return html`<admin-global-settings tab="required-fields"></admin-global-settings>`;
      case 'admin-settings-codelists':
        return html`<admin-global-settings tab="codelists" .codelistDomain=${this.codelistDomain}></admin-global-settings>`;
      case 'projects':
      default:
        return html`
          <project-dashboard 
            .projects=${this.projects}
            .parentProjectId=${this.initialParentId || ''}
            .projectToEdit=${this.projectToEdit}
            @project-selected=${(e) => this.navigateToProject(e.detail.projectId)}
            @project-created=${this.onProjectCreated}
            @modal-closed=${() => { this.initialParentId = null; this.projectToEdit = null; }}
            @create-template=${() => { this.navigate('/templates/new'); }}
          ></project-dashboard>
        `;
    }
  }

  render() {
    // Public invitation accept page — no auth or company scope required.
    if (window.location.pathname === '/accept-invite') {
      return html`<accept-invite></accept-invite>`;
    }

    // Public password-reset page (from the forgot-password e-mail link).
    if (window.location.pathname === '/reset-password') {
      return html`<reset-password></reset-password>`;
    }

    // Multi-company users pick a company before entering the app.
    if (this.needsCompanySelection) {
      const companies = auth.getCompanies();
      return html`
        <div style="min-height:100vh; display:flex; flex-direction:column; align-items:center; justify-content:center; padding:24px; background:var(--mv-bg,#f5f8fa);">
          <img src="${logoFullUrl}" alt="GeoVoice" style="width:230px; max-width:80%; height:auto; margin-bottom:28px;">
          <h2 style="margin:0 0 6px; font-size:1.6rem; font-weight:700; color:var(--mv-text); text-align:center;">Kies een organisatie</h2>
          <p style="margin:0 0 24px; color:var(--mv-text-sub,#6c757d); text-align:center;">Je hebt toegang tot meerdere bedrijven. Kies er één om verder te gaan.</p>
          ${this.companySwitchError ? html`<div style="max-width:440px; width:100%; padding:10px 14px; border-radius:8px; background:rgba(220,53,69,0.1); color:var(--mv-danger,#dc3545); margin-bottom:16px; text-align:center;">${this.companySwitchError}</div>` : ''}
          <div style="width:100%; max-width:440px; background:var(--mv-surface,#fff); border:1px solid var(--mv-border); border-radius:14px; box-shadow:0 8px 24px rgba(82,63,105,0.06); overflow:hidden;">
            ${companies.map((c, i) => html`
              <button @click=${() => this.chooseCompany(c.id)}
                style="display:flex; align-items:center; gap:14px; width:100%; padding:16px 18px; border:none; ${i > 0 ? 'border-top:1px solid var(--mv-border);' : ''} background:none; cursor:pointer; text-align:left; transition:background 0.15s;"
                @mouseenter=${(e) => e.currentTarget.style.background = 'var(--mv-surface-alt,#f8f9fa)'}
                @mouseleave=${(e) => e.currentTarget.style.background = 'none'}>
                <span style="flex-shrink:0; width:40px; height:40px; border-radius:10px; background:var(--mv-primary); color:#fff; display:flex; align-items:center; justify-content:center; font-weight:800; font-size:1.05rem;">${(c.name || '?').charAt(0).toUpperCase()}</span>
                <span style="flex:1; min-width:0;">
                  <span style="display:block; font-weight:600; color:var(--mv-text); white-space:nowrap; overflow:hidden; text-overflow:ellipsis;">${c.name}</span>
                  <span style="display:block; font-size:0.82rem; color:var(--mv-text-sub,#6c757d);">${this._roleLabel(c.role)}</span>
                </span>
                <span style="flex-shrink:0; color:var(--mv-text-sub,#adb5bd); display:flex;">${unsafeSVG(lucideIcon(ChevronRight, 20))}</span>
              </button>
            `)}
          </div>
          <button @click=${this.handleLogout}
            style="margin-top:20px; padding:9px 18px; border:none; background:none; color:var(--mv-text-sub,#6c757d); font-weight:600; cursor:pointer;">Uitloggen</button>
        </div>
      `;
    }

    if (!this.isAuthenticated) {
      return html`
        ${this.sessionExpiredMessage ? html`
          <div class="session-expired-banner">
            ${this.sessionExpiredMessage}
          </div>
        ` : ''}
        <login-form @login-success=${this.onLoginSuccess}></login-form>
      `;
    }

    return html`
      <div class="app-layout ${this._navOpen ? 'nav-open' : ''}">
        <!-- Labels staan in een tooltip zolang de balk smal is; @mousemove op de nav vangt
             ze allemaal op via het dichtstbijzijnde element met data-tip. -->
        <nav class="sidebar ${this._navOpen ? 'open' : ''}" data-tip-anchor
          @mousemove=${this._navOpen ? null : tipMove} @mouseleave=${tipHide}>
          <!-- Het logo is de weg terug naar het begin; dat is een gewende plek in vrijwel
               elke applicatie. Een <a> en geen knop, zodat openen in een nieuw tabblad werkt. -->
          <a class="logo" href="/" title="Naar projecten" @click=${this.handleLinkClick}>
            <img class="logo-icon" src="/logo-icon-white.png" alt="GeoVoice">
            <img class="logo-full" src="/logo-full-white.png" alt="GeoVoice">
          </a>
          <div class="nav-items">
            <div class="nav-section-title">Algemeen</div>
            <a href="/projects" data-tip="Projecten" data-tip-side="right" class="nav-item ${['projects', 'project-detail'].includes(this.currentView) ? 'active' : ''}" @click=${this.handleLinkClick}>
              <span class="nav-icon">${unsafeSVG(lucideIcon(Briefcase))}</span><span class="nav-label">Projecten</span>
            </a>
            <!-- Een gast ziet het team niet: hij hoort niet bij het bedrijf, hij is er te gast op
                 een project. De backend weigert de ledenlijst ook voor hem. -->
            ${!this._isGuest ? html`
            <a href="/team" data-tip="Team" data-tip-side="right" class="nav-item ${this.currentView === 'team' ? 'active' : ''}" @click=${this.handleLinkClick}>
              <span class="nav-icon">${unsafeSVG(lucideIcon(UsersRound))}</span><span class="nav-label">Team</span>
            </a>` : ''}
            <!-- Labopdrachten hoort bij GeoDesk. Anders dan het tabblad in een project, dat
                 blijft staan met een upgrademelding, verdwijnt dit item helemaal: in het
                 hoofdmenu hoort geen ingang naar een onderdeel dat je niet hebt. -->
            ${this._hasGeoDesk ? html`
            <a href="/labopdrachten" data-tip="Labopdrachten" data-tip-side="right" class="nav-item ${['lab-orders', 'lab-order'].includes(this.currentView) ? 'active' : ''}" @click=${this.handleLinkClick}>
              <span class="nav-icon">${unsafeSVG(lucideIcon(FlaskConical))}</span><span class="nav-label">Labopdrachten</span>
            </a>` : ''}
            <a href="/recorder" data-tip="Recorder App" data-tip-side="right" class="nav-item ${this.currentView === 'recorder' ? 'active' : ''}" @click=${this.handleLinkClick}>
              <span class="nav-icon">${unsafeSVG(lucideIcon(Smartphone))}</span><span class="nav-label">Recorder App</span>
            </a>

            ${auth.isSystemAdmin() ? html`
              <div class="nav-section-title">Beheer</div>
              <a href="/admin/users" data-tip="Gebruikers" data-tip-side="right" class="nav-item ${this.currentView === 'users' ? 'active' : ''}" @click=${this.handleLinkClick}>
                <span class="nav-icon">${unsafeSVG(lucideIcon(UserCheck))}</span><span class="nav-label">Gebruikers</span>
              </a>
              <a href="/admin/companies" data-tip="Bedrijven" data-tip-side="right" class="nav-item ${this.currentView === 'bedrijven' ? 'active' : ''}" @click=${this.handleLinkClick}>
                <span class="nav-icon">${unsafeSVG(lucideIcon(Building2))}</span><span class="nav-label">Bedrijven</span>
              </a>
              <a href="/admin/analysis" data-tip="Analyse" data-tip-side="right" class="nav-item ${this.currentView === 'analysis' ? 'active' : ''}" @click=${this.handleLinkClick}>
                <span class="nav-icon">${unsafeSVG(lucideIcon(Activity))}</span><span class="nav-label">Analyse</span>
              </a>
              <a href="/admin/validation" data-tip="Valideer model" data-tip-side="right" class="nav-item ${this.currentView === 'validation' ? 'active' : ''}" @click=${this.handleLinkClick}>
                <span class="nav-icon">${unsafeSVG(lucideIcon(Check))}</span><span class="nav-label">Valideer model</span>
              </a>
              <a href="/admin/settings/recorder" data-tip="Beheer-instellingen" data-tip-side="right" class="nav-item ${this.currentView.startsWith('admin-settings-') ? 'active' : ''}" @click=${this.handleLinkClick}>
                <span class="nav-icon">${unsafeSVG(lucideIcon(Settings2))}</span><span class="nav-label">Instellingen</span>
              </a>
            ` : ''}
          </div>

          <div class="nav-bottom">
            <a href=${this._settingsHome} data-tip="Instellingen" data-tip-side="right"
              class="nav-item ${[...COMPANY_SETTINGS_VIEWS, 'personal-settings', 'personal-appearance'].includes(this.currentView) ? 'active' : ''}"
              @click=${this.handleLinkClick}>
              <span class="nav-icon">${unsafeSVG(lucideIcon(Settings))}</span><span class="nav-label">Instellingen</span>
            </a>
          </div>

          <button class="rail-toggle" data-tip-side="right" data-tip=${this._navOpen ? 'Inklappen' : 'Uitklappen'}
            @mousemove=${tipMove} @mouseleave=${tipHide}
            @click=${() => this._toggleNav()} aria-label=${this._navOpen ? 'Menu inklappen' : 'Menu uitklappen'}>
            ${unsafeSVG(lucideIcon(this._navOpen ? ChevronLeft : ChevronRight, 15))}
          </button>
        </nav>
        
        <main class="content">
          ${this._renderTopbar()}
          ${this._renderCrumbs()}
          <div class="content-scroll ${this._fancyBg ? 'fancy-bg' : ''}">
            ${this.renderCurrentView()}
          </div>
        </main>

        <nav class="bottom-nav">
          <a href="/projects" class="nav-item ${['projects', 'project-detail'].includes(this.currentView) ? 'active' : ''}" @click=${this.handleLinkClick}>
            ${unsafeSVG(lucideIcon(Folder, 20))}
            <span>Projecten</span>
          </a>

          ${!this._isGuest ? html`
          <a href="/team" class="nav-item ${this.currentView === 'team' ? 'active' : ''}" @click=${this.handleLinkClick}>
            ${unsafeSVG(lucideIcon(UsersRound, 20))}
            <span>Team</span>
          </a>` : ''}

          ${this._hasGeoDesk ? html`
          <a href="/labopdrachten" class="nav-item ${['lab-orders', 'lab-order'].includes(this.currentView) ? 'active' : ''}" @click=${this.handleLinkClick}>
            ${unsafeSVG(lucideIcon(FlaskConical, 20))}
            <span>Labopdrachten</span>
          </a>` : ''}

          <a href="/recorder" class="nav-item ${this.currentView === 'recorder' ? 'active' : ''}" @click=${this.handleLinkClick}>
            ${unsafeSVG(lucideIcon(Smartphone, 20))}
            <span>Recorder</span>
          </a>
          ${this._canManageRecorder ? html`
            <a href="/settings/recorder" class="nav-item ${['recorder-settings', 'company-settings', 'personal-settings', 'personal-appearance', 'templates', 'template-editor', 'report-settings'].includes(this.currentView) ? 'active' : ''}" @click=${this.handleLinkClick}>
              ${unsafeSVG(lucideIcon(Settings2, 20))}
              <span>Instellingen</span>
            </a>
          ` : ''}

          ${auth.isSystemAdmin() ? html`
            <a href="/admin/users" class="nav-item ${this.currentView === 'users' ? 'active' : ''}" @click=${this.handleLinkClick}>
              ${unsafeSVG(lucideIcon(Users, 20))}
              <span>Beheer</span>
            </a>
            <a href="/admin/companies" class="nav-item ${this.currentView === 'bedrijven' ? 'active' : ''}" @click=${this.handleLinkClick}>
              ${unsafeSVG(lucideIcon(Building2, 20))}
              <span>Bedrijven</span>
            </a>
          ` : ''}
          
        </nav>
      </div>
    `;
  }
}

customElements.define('app-main', AppMain);
